import { ReactNode, useState } from 'react'
import { styled, CSS } from 'theme'
import { Box, Button, ColorProps, Flex, Text } from '.'
import { Close } from 'components/icons'
// import { GroupIconCss } from 'components/cards/components'
import { SvgCircleWrap } from 'components/svg'
import { GroupIconCss } from 'theme/globalStyleCss'
import { h5Styles } from './styleText'

export const BadgeCss = styled(Box, {
  // Reset
  'position': 'relative',
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'space-between',
  'flexShrink': 0,
  'border': '1px dashed $inputBorder',
  'p': '$space$14',
  'transitionButton': '$transition$easeOutQuint',
  // Custom
  'fontSize': '$16',
  'lineHeight': '$lineHeight$24',
  'borderRadius': '$radii$sm',
  'fontWeight': '$normal',

  '&:disabled': {
    backgroundColor: '$primaryDisabled',
    color: '$disabledText',
    cursor: 'not-allowed',
    borderColor: '$primaryDisabled',
  },

  'variants': {
    color: {
      primary: {
        'backgroundColor': '$primary25',
        'color': '$primary700',
        'borderColor': '$primary300',

        '&:hover': {
          borderColor: '$primary400',
        },
      },
      secondary: {
        'backgroundColor': 'transparent',
        'color': '$gray600',
        'borderColor': '$gray700',
        '&:hover': {
          backgroundColor: 'transparent',
          color: 'gray700',
          borderColor: '$primaryBgHover',
        },
        '&:disabled': {
          backgroundColor: 'transparent',
          color: 'gray400',
          borderColor: '$gray400',
        },
      },
      gray: {
        'backgroundColor': '$tagGrayBg',
        'color': '$titleGray',
        'borderColor': '$border',
        '&:hover': {
          backgroundColor: 'tagGrayBg',
          borderColor: '$border',
          color: '$titleGray',
        },
      },
      warning: {
        'backgroundColor': '$warning600',
        'color': '$whiteBase',
        'borderColor': '$warning600',
        '&:hover': {
          backgroundColor: '$warning700',
          borderColor: '$warning700',
        },
      },
      error: {
        'backgroundColor': '$error25',
        'color': '$whiteBase',
        'borderColor': '$error300',
        '&:hover': {
          backgroundColor: '$error25',
          borderColor: '$error300',
        },
      },
      success: {
        'backgroundColor': '$success600',
        'color': '$whiteBase',
        'borderColor': '$success600',
        '&:hover': {
          backgroundColor: '$success700',
          borderColor: '$success700',
        },
      },
      transparent: {
        'backgroundColor': '$inputBg',
        'color': '$inputLabel',
        'borderColor': '$inputBorder',
        '&:hover': {
          borderColor: '$primarySolidHover',
        },
      },
      black: {
        'backgroundColor': '$inputBg',
        'color': '$inputLabel',
        'borderColor': '$inputBorder',
        '&:hover': {
          borderColor: '$primarySolidHover',
        },
      },
      white: {
        'backgroundColor': '$inputBg',
        'color': '$inputLabel',
        'borderColor': '$inputBorder',
        '&:hover': {
          borderColor: '$primarySolidHover',
        },
      },
      disabled: {
        'backgroundColor': '$inputBg',
        'color': '$inputLabel',
        'borderColor': '$inputBorder',
        '&:hover': {
          borderColor: '$primarySolidHover',
        },
      },
    },
    asField: {
      primary: {
        'backgroundColor': '$primary25',
        'color': '$primaryBase',
        'border': '1px solid $primary300',
        '&:hover': {
          borderColor: '$primary400',
        },
      },
      secondary: {
        'backgroundColor': '$transparent',
        'color': '$transparent',

        'border': '1px solid $gray200',
        '&:hover': {
          borderColor: '$gray200',
        },
      },
    },
  },
  'defaultVariants': {},
})

interface BadgeProps {
  color?: ColorProps
  css?: CSS
  icon?: ReactNode
  asCircleIcon?: boolean
  alignIcon?: 'stretch' | 'center' | 'end' | 'start' | 'baseline' | undefined
  label?: {
    mainHeading?: {
      text: ReactNode
      css?: CSS
    }
    subHeading?: {
      text?: string | ReactNode
      css?: CSS
    }
    labelColumn?: boolean
  }
  button?: {
    text?: string
    showClose?: boolean
    css?: CSS
    optionalItem?: ReactNode
  }
  onClick?: (arg: any) => void
  asField?: 'primary' | 'secondary'
  asSpan?: string
  optionalContent?: ReactNode
}

export const Badges = ({
  icon,
  button,
  color,
  label,
  asField,
  asCircleIcon,
  alignIcon,
  asSpan,
  optionalContent,
  css,
  ...rest
}: BadgeProps) => {
  const { mainHeading, subHeading, labelColumn } = label ?? {}
  const [isVisible, setIsVisible] = useState(true)
  const handleClose = () => {
    setIsVisible(false)
  }

  return (
    <>
      {isVisible && (
        <BadgeCss color={color} {...rest} asField={asField} css={{ ...css }}>
          <Flex align={alignIcon || 'center'} css={{ width: '100%' }}>
            {icon && (
              <>
                {asCircleIcon ? (
                  <SvgCircleWrap
                    color={color}
                    css={{
                      ...GroupIconCss,
                      ml: '-$space$4',
                      my: '-$space$4',
                      mr: '$space$15',
                    }}
                  >
                    {icon}
                  </SvgCircleWrap>
                ) : (
                  <>{icon}</>
                )}
              </>
            )}

            {!optionalContent ? (
              <>
                <Flex
                  direction={labelColumn ? 'column' : 'row'}
                  align="baseline"
                >
                  <Text
                    color={color}
                    as="h5"
                    css={{ pr: '$space$6', ...mainHeading?.css }}
                  >
                    {mainHeading?.text}{' '}
                    {asSpan ? (
                      <Text as="span" css={{ color: '$gray500', ...h5Styles }}>
                        {asSpan}
                      </Text>
                    ) : null}
                  </Text>
                  <Text
                    color={!subHeading ? color : undefined}
                    as="p"
                    css={{ ...subHeading?.css }}
                  >
                    {subHeading?.text}
                  </Text>
                </Flex>
              </>
            ) : (
              <>
                <Flex align={'center'} css={{ width: '100%' }}>
                  {optionalContent}
                </Flex>
              </>
            )}
          </Flex>
          {!!button && (
            <Flex align={'center'} css={button?.css}>
              {!!button?.text && <Button>{button?.text}</Button>}
              {button?.showClose && (
                <Button
                  type="button"
                  transparent="asIcon"
                  onClick={handleClose}
                  children={<Close />}
                  css={{ p: '$space$10', ml: '$space$8' }}
                />
              )}
            </Flex>
          )}
          {!!button?.optionalItem && button?.optionalItem}
        </BadgeCss>
      )}
    </>
  )
}
